// FTEDN Creator — Chat Page (messages with team)
import { useEffect, useState, useRef } from 'react';
import { useChatStore } from '../../stores/chat.store';
import { useUserStore } from '../../stores/user.store';
import { getChannelDisplayName, validateMessage } from '../../stores/chat.store';
import { useToast } from '../../components/ui/Toast';
import { Skeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import './ChatPage.css';

export function ChatPage() {
  const { channels, messages, activeChannelId, loading, fetchChannels, setActiveChannel, sendMessage } = useChatStore();
  const { profile } = useUserStore();
  const { addToast } = useToast();
  const [newMsg, setNewMsg] = useState('');
  const messagesEnd = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!activeChannelId || !newMsg.trim()) return;

    const validation = validateMessage(newMsg);
    if (!validation.valid) {
      addToast({ type: 'warning', title: 'Oops', message: validation.error });
      return;
    }

    const ok = await sendMessage(activeChannelId, newMsg);
    if (ok) {
      setNewMsg('');
    } else {
      addToast({ type: 'error', title: 'Failed', message: 'Could not send message' });
    }
  };

  const userId = profile?.id ?? '';
  const activeChannel = channels.find(c => c.id === activeChannelId);

  return (
    <div className="chat-page page-enter">
      <div className="chat-page-header">
        <h1>✉️ Messages</h1>
        <p className="chat-page-subtitle">Chat with the FTEDN team</p>
      </div>

      {loading ? (
        <Skeleton variant="card" count={3} />
      ) : channels.length === 0 ? (
        <EmptyState
          icon="✉️"
          title="No conversations"
          description="When the FTEDN team reaches out, your messages will appear here."
        />
      ) : (
        <div className="chat-page-layout">
          {/* Channel list */}
          <div className="chat-page-channels">
            {channels.map(ch => {
              const displayName = getChannelDisplayName(ch, userId, {});
              return (
                <button
                  key={ch.id}
                  className={`chat-channel-btn ${activeChannelId === ch.id ? 'active' : ''}`}
                  onClick={() => setActiveChannel(ch.id)}>
                  <span className="chat-channel-icon">{ch.type === 'dm' ? '👤' : '👥'}</span>
                  <div className="chat-channel-info">
                    <span className="chat-channel-name">{displayName}</span>
                    <span className="chat-channel-type">{ch.type}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Messages */}
          <div className="chat-page-messages">
            {!activeChannelId ? (
              <div className="chat-empty-msg">
                <span>💬</span>
                <p>Select a conversation</p>
              </div>
            ) : (
              <>
                <div className="chat-msg-header">
                  <h3>{activeChannel ? getChannelDisplayName(activeChannel, userId, {}) : 'Chat'}</h3>
                </div>
                <div className="chat-msg-list">
                  {messages.filter(m => !m.hidden).map(m => (
                    <div key={m.id} className={`chat-bubble ${m.senderId === userId ? 'mine' : 'theirs'}`}>
                      <p>{m.text}</p>
                      <span className="chat-bubble-time">{new Date(m.createdAt).toLocaleTimeString()}</span>
                    </div>
                  ))}
                  <div ref={messagesEnd} />
                </div>
                <div className="chat-compose">
                  <input
                    className="neo-input chat-compose-input"
                    placeholder="Type a message..."
                    value={newMsg}
                    onChange={e => setNewMsg(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSend()}
                  />
                  <button className="neo-btn neo-btn--primary" onClick={handleSend}>Send</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
